import { useCallback, useState, useSyncExternalStore } from 'react';

const PARAM = 'inspect';

// Notify subscribers when we manually change the URL
const listeners = new Set<() => void>();
function notifyUrlChange() {
  listeners.forEach(cb => cb());
}

function subscribeToUrl(cb: () => void) {
  listeners.add(cb);
  window.addEventListener('popstate', cb);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('popstate', cb);
  };
}

function getInspectParam() {
  return new URLSearchParams(window.location.search).get(PARAM);
}

/**
 * Replaces a single search param in the URL without going through React Router.
 * This avoids re-rendering the entire router tree.
 */
function setUrlParam(key: string, value: string | null) {
  const url = new URL(window.location.href);
  if (value === null) {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, value);
  }
  window.history.replaceState(null, '', url.toString());
  notifyUrlChange();
}

/**
 * Lightweight hook for the parent layout — reads `?inspect` from the URL
 * without subscribing to React Router state. Only provides `open()`.
 */
export const useEntityInspector = () => {
  const inspectValue = useSyncExternalStore(subscribeToUrl, getInspectParam);
  const isOpen = typeof inspectValue === 'string';

  const open = useCallback(() => {
    setUrlParam(PARAM, '');
  }, []);

  return { isOpen, open };
};

/**
 * Hook used inside EntityInspector — manages tab selection and close.
 * All URL updates bypass React Router to avoid page flicker.
 */
export const useInspectorParams = () => {
  const initialTab = getInspectParam() || 'overview';
  const [selectedTab, setSelectedTab] = useState(initialTab);

  const close = useCallback(() => {
    setUrlParam(PARAM, null);
  }, []);

  const selectTab = useCallback((tab: string) => {
    setSelectedTab(tab);
    setUrlParam(PARAM, tab);
  }, []);

  return { selectedTab, close, selectTab };
};
